import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FileSystemDirectoryEntry, FileSystemFileEntry, NgxFileDropEntry, NgxFileDropModule } from 'ngx-file-drop';
import { BehaviorSubject, catchError, concatMap, first, mergeMap, Observable, of, tap } from 'rxjs';
import { ActivityCodeEnum, CategoryEnum, NotificationAreaLodgementTypeEnum, PurposeEnum } from 'src/app/models/shared-models';
import { GlobalValidationMessageComponent } from 'src/app/shared/global-validation-message/global-validation-message.component';
import { IMapComponentModel, IMapEventEmitterModel } from 'src/app/shared/map/map-component-model';
import { MapComponent } from 'src/app/shared/map/map.component';
import { MapService } from 'src/app/shared/services/map.service';
import { BaseWizardComponent } from '../base-wizard.component';
import { CategoryPurposeDataModel, IActivityStorageDataModel, ICategoryPurposeDataModel, ICoastalResponseMessages, ILotPlanModel, INotificationAreaLimitModel, INotificationAreaModel, IPurposeModel, PurposeModel } from '../models/veg-wizard-models';
import { FileManagementService } from '../services/file-management.service';
import { Logger } from '../services/logger-service';
import { NotificationAreaService } from '../services/notification-area-service';
import { UtilService } from '../services/util.service';
import { VegWizardDataService } from '../services/veg-wizard-data.service';

@UntilDestroy()
@Component({
  selector: 'app-notification-area',
  templateUrl: './notification-area.component.html',
  styleUrls: ['./notification-area.component.css'],
  imports: [CommonModule, ReactiveFormsModule, GlobalValidationMessageComponent, MapComponent, NgxFileDropModule],
})
export class NotificationAreaComponent extends BaseWizardComponent implements OnInit {
  private readonly formBuilder = inject(FormBuilder);
  private readonly notificationAreaService = inject(NotificationAreaService);
  private readonly fileManagementService = inject(FileManagementService);
  private readonly vwds = inject(VegWizardDataService);
  private readonly mapService = inject(MapService);
  private readonly utilService = inject(UtilService);
  private readonly logger = inject(Logger);

  public islodgmentinLotPlan$: BehaviorSubject<boolean | null> = new BehaviorSubject<boolean | null>(true);
  public areAreasOverlapping$: BehaviorSubject<boolean | null> = new BehaviorSubject<boolean | null>(false);

  nextButtonClicked = false;
  validMapMimeType = '';
  form: FormGroup;

  // form getters
  get areaControl() {
    return this.form.get('area');
  }
  get howToProvideControl() {
    return this.form.get('howToProvide');
  }
  get mapProvidedControl() {
    return this.form.get('mapProvided');
  }
  get isCoastalResponseControl() {
    return this.form.get('isCoastalResponse');
  }
  get lodgementShapeGeoJsonControl() {
    return this.form.get('lodgementShapeGeoJson');
  }
  get lotPlanShapeGeoJsonControl() {
    return this.form.get('lotPlanShapeGeoJson');
  }
  get isHarvestedWithinTenYearsControl() {
    return this.form.get('isHarvestedWithinTenYears');
  }
  get isInRestrictedClearingAreaControl() {
    return this.form.get('isInRestrictedClearingArea');
  }
  get areaWidthControl() {
    return this.form.get('areaWidth');
  }
  get isKoalaAreaControl() {
    return this.form.get('isKoalaArea');
  }

  lotOnPlan: ILotPlanModel = {} as ILotPlanModel;
  purpose: IPurposeModel = {} as PurposeModel;
  categories: ICategoryPurposeDataModel[] = [{} as CategoryPurposeDataModel];
  activities: IActivityStorageDataModel = {} as IActivityStorageDataModel;

  readonly koalaHabitatLimitAreaHectares = 10;
  areaLimit: INotificationAreaLimitModel = {} as INotificationAreaLimitModel;
  estimatedAreaOptional = false;
  isNFPorFodder = false;
  lastKnownArea = 0;
  mapError = '';
  maximumNumberOfGPSPoints = 20;
  minimumNumberOfGPSPoints = 5;
  notificationAreaCalcNeedsUpdating = false;
  notificationAreaLimitMessage = '';
  gpsPointsOptional = false;
  requiresIsCoastalResponse = false;
  requiresExtractiveIndustryCatBMessage = false;
  requiresHarvestedWithin10YearsResponse = false;
  requiresClearingAreaQuestion = false;
  requiresAreaInHectares = true;
  showNotificationAreaLimitMessage = false;
  mapFileNames: string[] = [];
  notificationAreaFiles: NgxFileDropEntry[] = [];
  linearMapSelectionRequired = false;
  currentLineLength = 0;
  currentLineWidth = 0;
  mapComponentModel: IMapComponentModel = {} as IMapComponentModel;

  // validation properties
  howToProvideValid = true;
  spatialMapPointsPresentValid = true;
  isCoastalResponseValid = true;
  isHarvestedWithinTenYearsValid = true;
  isCostalResponsePresentValid = true;
  isInRestrictedClearingAreaValid = true;
  areaPresentValid = true;
  areaRangeValid = true;
  gpsHasValueValid = true;
  areaInHectares = 0;
  mapUploadValid = true;
  isKoalaAreaValid = false;
  koalaClearingMessage: string | undefined;
  coastalResponseMessages: ICoastalResponseMessages = {} as ICoastalResponseMessages;
  isAreaWidthValid = false;
  isAreaWidthDirty = false;
  mapLodgementValid = true;

  constructor() {
    super();
    this.lotOnPlan = this.vwds.getLotPlanModel() || ({} as ILotPlanModel);
    this.purpose = this.vwds.getPurpose()?.purpose || new PurposeModel();
    this.requiresHarvestedWithin10YearsResponse = this.purpose.purposeCode === PurposeEnum.fodder;
    this.categories = this.vwds.getCategories();
    this.activities = this.vwds.getActivities() || ({} as IActivityStorageDataModel);
    this.form = this.formBuilder.group({});
  }

  ngOnInit(): void {
    this.nextButtonClicked = false;
    const notificationArea = this.vwds.getNotificationArea();

    this.validMapMimeType = this.fileManagementService.validMapMimeType.toString();
    this.requiresClearingAreaQuestion = this.notificationAreaService.requiresClearingAreaQuestion(this.purpose);

    this.form = this.formBuilder.group({
      howToProvide: [notificationArea?.howToProvide],
      isHarvestedWithinTenYears: [notificationArea?.isHarvestedWithinTenYears],
      isCoastalResponse: [notificationArea?.isCoastalResponse],
      isInRestrictedClearingArea: [notificationArea?.isInRestrictedClearingArea],
      lodgementShapeGeoJson: [notificationArea?.lodgementShapeGeoJson],
      lotPlanShapeGeoJson: [notificationArea?.lotPlanShapeGeoJson],
      area: [notificationArea?.area || 0],
      lotOnPlanArea: [this.lotOnPlan.area],
      mapProvided: [notificationArea?.mapProvided],
      isKoalaArea: [notificationArea?.isKoalaLotPlanArea],
      areaWidth: [notificationArea?.areaWidth || 0.1],
    });

    this.currentLineLength = notificationArea?.lineLength ?? 0;
    this.currentLineWidth = notificationArea?.areaWidth ?? 0;
    this.lastKnownArea = this.calcHecters(this.currentLineLength, this.currentLineWidth);
    this.areaInHectares = this.lastKnownArea;

    this.mapFileNames = notificationArea?.mapFileNames ?? [];

    this.isNFPorFodder = this.purpose.purposeCode === PurposeEnum.nativeForestPractice || this.purpose.purposeCode === PurposeEnum.fodder;

    this.requiresIsCoastalResponse = this.notificationAreaService.requiresIsCoastalResponse(this.categories, this.activities);

    this.requiresExtractiveIndustryCatBMessage = this.purpose.purposeCode === PurposeEnum.extractiveIndustry && this.categories.length >= 2 && this.categories.some((category) => category.categoryCode === CategoryEnum.catB);

    const activitiesDetails = this.vwds.getActivities();
    if (!!activitiesDetails && activitiesDetails.selectedActivities && activitiesDetails.selectedActivities.length) {
      const selectedActivity = activitiesDetails.selectedActivities[0];
      this.minimumNumberOfGPSPoints = selectedActivity.minimumNumberOfGPSPointsOverride || 5;
      this.maximumNumberOfGPSPoints = selectedActivity.maximumNumberOfGPSPointsOverride || 20;
      this.requiresAreaInHectares = selectedActivity.requiresAreaInHectares;
      this.estimatedAreaOptional = selectedActivity.estimatedAreaOptional;
      this.gpsPointsOptional = selectedActivity.gpsPointsOptional;

      if (this.purpose.purposeCode === PurposeEnum.infrastructure) {
        const linearInfraActivities = [ActivityCodeEnum.boundaryFences, ActivityCodeEnum.contourBanks, ActivityCodeEnum.fireManagementLines, ActivityCodeEnum.internalFences, ActivityCodeEnum.irrigationChannels, ActivityCodeEnum.tracks, ActivityCodeEnum.otherLinearInfrastructure];

        if (linearInfraActivities.some((a) => a === selectedActivity.activityCode)) {
          this.linearMapSelectionRequired = true;
        }
      }
    }

    this.mapComponentModel = {
      lot: this.lotOnPlan.lot,
      plan: this.lotOnPlan.plan,
      lotPlanShapeGeoJson: notificationArea?.lotPlanShapeGeoJson,
      lodgementShapeGeoJson: notificationArea?.lodgementShapeGeoJson,
      lineSelectMode: this.linearMapSelectionRequired,
    } as IMapComponentModel;

    this.setCoastalResponseAndGetLatestAreaCap$().pipe(first(), untilDestroyed(this)).subscribe();

    this.mapProvidedControl?.setValue(this.mapFileNames.length > 0);
    this.performValidations();

    // observe and perform validations, or change variables as required
    this.form.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.performValidations();
    });
  }

  performValidations() {
    this.coastalResponseMessages = this.notificationAreaService.getIsCoastalResponseMessage(this.purpose, this.categories, this.activities, this.isCoastalResponseControl?.value);

    this.spatialMapPointsPresentValid = this.gpsPointsOptional || this.howToProvideControl?.value !== NotificationAreaLodgementTypeEnum.spatialDataCapture || this.lodgementShapeAndLotPlanShapeAreValid();

    this.isCoastalResponseValid = this.notificationAreaService.isCoastalResponseValid(this.purpose, this.categories, this.activities, this.isCoastalResponseControl?.value);

    this.areaRangeValid = this.validateAreaRange();

    this.areaPresentValid = !this.requiresAreaInHectares || this.estimatedAreaOptional || !!this.areaControl?.value;

    this.showNotificationAreaLimitMessage = this.notificationAreaLimitMessageShouldShow();

    this.notificationAreaLimitMessage = this.getNotificationAreaLimitMessage();

    this.howToProvideValid = this.gpsPointsOptional || !!this.howToProvideControl?.value;

    this.isCostalResponsePresentValid = !this.requiresIsCoastalResponse || this.isCoastalResponseControl?.value !== null;

    this.isHarvestedWithinTenYearsValid = !this.requiresHarvestedWithin10YearsResponse || (!!this.isHarvestedWithinTenYearsControl && this.isHarvestedWithinTenYearsControl.value === 'No');

    this.isInRestrictedClearingAreaValid = !this.requiresClearingAreaQuestion || (this.requiresClearingAreaQuestion && this.notificationAreaService.checkIfClearingAreaQuestionIsValid(this.purpose, this.isInRestrictedClearingAreaControl?.value));

    this.gpsHasValueValid = this.howToProvideControl?.value !== null && (this.howToProvideControl?.value === NotificationAreaLodgementTypeEnum.manualUpload || (this.areaControl?.value > 0 && this.howToProvideControl?.value === NotificationAreaLodgementTypeEnum.spatialDataCapture));

    this.mapUploadValid = this.gpsPointsOptional || this.howToProvideControl?.value !== NotificationAreaLodgementTypeEnum.manualUpload || this.mapProvidedControl?.value;

    this.isAreaWidthValid = !this.linearMapSelectionRequired || this.currentLineWidth > 0;

    if (this.howToProvideControl?.value !== 'gps') this.isAreaWidthValid = true;

    this.mapLodgementValid = this.howToProvideControl?.value !== NotificationAreaLodgementTypeEnum.spatialDataCapture || (!this.areAreasOverlapping$.getValue() && this.islodgmentinLotPlan$.getValue() !== false);

    this.determineKoalaAreaLimitValidationAndMessage();

    // clear any validation errors to start with
    this.howToProvideControl?.setErrors(null);
    this.isCoastalResponseControl?.setErrors(null);
    this.isHarvestedWithinTenYearsControl?.setErrors(null);
    this.mapProvidedControl?.setErrors(null);
    this.isInRestrictedClearingAreaControl?.setErrors(null);
    this.areaControl?.setErrors(null);
    this.isKoalaAreaControl?.setErrors(null);
    this.areaWidthControl?.setErrors(null);
    this.lodgementShapeGeoJsonControl?.setErrors(null);

    if (!this.howToProvideValid) this.howToProvideControl?.setErrors({ Required: true });
    if (!this.isCoastalResponseValid || !this.isCostalResponsePresentValid) this.isCoastalResponseControl?.setErrors({ Required: true });
    if (!this.isHarvestedWithinTenYearsValid)
      this.isHarvestedWithinTenYearsControl?.setErrors({
        'isHarvestedWithinTenYearsValid Required': true,
      });
    if (!this.gpsHasValueValid) this.mapProvidedControl?.setErrors({ 'gpsHasValueValid Required': true });
    if (!this.mapUploadValid) this.mapProvidedControl?.setErrors({ 'mapUploadValid Required': true });
    if (!this.isInRestrictedClearingAreaValid)
      this.isInRestrictedClearingAreaControl?.setErrors({
        'isInRestrictedClearingAreaValid Required': true,
      });
    if (!this.areaRangeValid) this.areaControl?.setErrors({ 'areaRangeValid Required': true });
    if (!this.isAreaWidthValid) this.areaWidthControl?.setErrors({ 'isAreaWidthValid required': true });
    if (!this.mapLodgementValid) this.lodgementShapeGeoJsonControl?.setErrors({ 'Map Lodgement is invalid': true });
    if (!this.isKoalaAreaValid && this.koalaClearingMessage) this.isKoalaAreaControl?.setErrors({ 'koalaAreaValid Required': true });
  }

  lodgementShapeAndLotPlanShapeAreValid(): boolean {
    if (!!this.lodgementShapeGeoJsonControl?.value && !!this.lotPlanShapeGeoJsonControl?.value) {
      if (!this.lotPlanShapeGeoJsonControl?.value.includes('geometry')) return false;
      if (this.lodgementShapeGeoJsonControl?.value.includes('geometry') || this.lodgementShapeGeoJsonControl?.value.includes('point')) {
        return true;
      }
    }
    return false;
  }

  handleMapDataEmitEvent(model: IMapEventEmitterModel) {
    console.log(model);
    this.currentLineLength = this.linearMapSelectionRequired ? this.mapService.calculateTotalLineLength(model.landClearingLayer) : 0;
    this.isAreaWidthDirty = true;

    const clearingArea = this.linearMapSelectionRequired ? this.calcHecters(this.currentLineWidth, this.currentLineLength) : this.mapService.calculateTotalArea(model.landClearingLayer);
    this.lodgementShapeGeoJsonControl?.setValue(JSON.stringify(model.landClearingGeoJson));
    this.lotPlanShapeGeoJsonControl?.setValue(JSON.stringify(model.lotPlanGeoJson));
    this.areaControl?.setValue(clearingArea);

    if (!this.linearMapSelectionRequired) {
      this.areAreasOverlapping$.next(this.mapService.doAnyAreasOverlap(model.landClearingGeoJson.features));
      this.islodgmentinLotPlan$.next(this.mapService.areAreasInBounds(model.landClearingGeoJson.features, model.lotPlanGeoJson.features));
    } else {
      this.islodgmentinLotPlan$.next(this.mapService.areLinesInBounds(model.landClearingGeoJson.features, model.lotPlanGeoJson.features));
    }
    this.lastKnownArea = clearingArea;

    if (this.areAreasOverlapping$.getValue() || !this.islodgmentinLotPlan$.getValue())
      this.lodgementShapeGeoJsonControl?.setErrors({
        'Map Lodgement is invalid': true,
      });
    else this.lodgementShapeGeoJsonControl?.setErrors(null);
  }

  validateAreaRange() {
    const areaNotRequired = !this.requiresAreaInHectares || (this.estimatedAreaOptional && !this.areaControl?.value);
    const areaProvidedIsValid = this.areaLimit != null && 0 < this.areaControl?.value && this.areaControl?.value <= this.areaLimit.remainingAreaLimitInHectares;
    const areaRangeValid = areaNotRequired || areaProvidedIsValid;

    return areaRangeValid;
  }

  getNotificationAreaLimitMessage(): string {
    return this.notificationAreaService.getNotificationAreaLimitErrorMessage(this.areaLimit, this.purpose.purposeCode, this.isKoalaAreaValid, this.koalaClearingMessage);
  }

  notificationAreaLimitMessageShouldShow(): boolean {
    return (!!this.areaLimit && this.areaPresentValid && !this.areaRangeValid && this.areaLimit.remainingAreaLimitInHectares > 0 && !this.notificationAreaCalcNeedsUpdating) || (!this.isKoalaAreaValid && !!this.koalaClearingMessage);
  }

  public updateWidth() {
    this.isAreaWidthDirty = true;
    this.currentLineWidth = this.areaWidthControl?.value ?? 0;
    this.lastKnownArea = this.calcHecters(this.currentLineLength, this.currentLineWidth);
    this.areaControl?.setValue(this.lastKnownArea);
  }

  //Use to convert line lengths with widths into hectare area.
  calcHecters(length: number, width: number): number {
    return Number.parseFloat(((length * width) / 10000).toFixed(2));
  }

  setCoastalResponseAndGetLatestAreaCap$(): Observable<boolean> {
    const isCoastalResponse = this.isCoastalResponseControl?.value;

    return this.notificationAreaService.getNotificationAreaLimitCap$(isCoastalResponse).pipe(
      tap((limit) => {
        //Area limit is based on the selected coastal/non-coastal response,
        this.areaLimit = limit;
        this.notificationAreaCalcNeedsUpdating = false;
      }),
      //this value needs to be stored on server and re-validate to get areaCap
      mergeMap(() => this.vwds.setIsCoastalResponse$(isCoastalResponse)),
    );
  }

  removeMapFile(mapToBeRemoved: string) {
    const fileNamesToKeep: string[] = [];

    if (this.notificationAreaFiles && this.notificationAreaFiles.length > 0) {
      const foundMatchingFile = this.notificationAreaFiles.find((x) => x.fileEntry.name === mapToBeRemoved);

      if (foundMatchingFile) {
        this.notificationAreaFiles.splice(this.notificationAreaFiles.indexOf(foundMatchingFile), 1);

        for (let index = 0; index < this.notificationAreaFiles.length; index++) {
          fileNamesToKeep.push(this.notificationAreaFiles[index].fileEntry.name);
        }

        this.mapFileNames = fileNamesToKeep;
      }
    } else {
      // handle events where the page has been reloaded (when user click on previous screen)
      const foundMatchingFile = this.mapFileNames.find((x) => x === mapToBeRemoved);

      if (foundMatchingFile) {
        this.mapFileNames.splice(this.mapFileNames.indexOf(foundMatchingFile), 1);

        for (let index = 0; index < this.mapFileNames.length; index++) {
          fileNamesToKeep.push(this.mapFileNames[index]);
        }

        this.mapFileNames = fileNamesToKeep;
      }
    }

    //post to server on what map attachments need to be kept
    this.fileManagementService
      .removeMapFiles$(fileNamesToKeep)
      .pipe(
        first(),
        untilDestroyed(this),
        tap((response: unknown) => {
          if (!!response && Array.isArray(response) && response.length > 0) {
            this.mapProvidedControl?.setValue(true);
            this.mapError = '';
          } else {
            this.mapProvidedControl?.setValue(false);
            this.mapError = '';
          }
        }),
      )
      .subscribe(() => {
        const formModel = this.getNotificationAreaFormData();
        return this.notificationAreaService.sessionSetNotificationArea$(formModel);
      });
  }

  removeAll() {
    this.fileManagementService
      .removeMapFiles$([])
      .pipe(
        first(),
        untilDestroyed(this),
        tap(() => {
          this.mapProvidedControl?.setValue(false);
          this.mapError = '';

          this.mapFileNames = [];
          this.notificationAreaFiles = [];
        }),
      )
      .subscribe(() => {
        const formModel = this.getNotificationAreaFormData();
        console.log('removeAll() > model = ', formModel);
        return this.notificationAreaService.sessionSetNotificationArea$(formModel);
      });
  }

  resetArea() {
    const val = this.howToProvideControl?.value === NotificationAreaLodgementTypeEnum.spatialDataCapture ? this.lastKnownArea : 0;
    this.areaControl?.setValue(val);
  }

  uploadOrDroppedFiles(files: NgxFileDropEntry[]) {
    if (!files) {
      this.logger.warning('Some issues with the files content - please check');
      return;
    }

    const tenMegabytes = 10 * 1024 * 1024;
    let allFilesValid = true;
    const errorFilesCountMoreThanFive = [];
    const errorFilesOverTenMb: string[] = [];
    const errorFilesWithIncorrectMimeTypes: string[] = [];
    const errorFilesWithIllegalCharacters: string[] = [];
    const validFilesToUpload: NgxFileDropEntry[] = [];

    const concatFiles = this.notificationAreaFiles.concat(files);
    const uniqueFilesBasedOnName = [...new Map(concatFiles.map((item) => [item.fileEntry.name, item])).values()];
    this.notificationAreaFiles = uniqueFilesBasedOnName;

    if (this.notificationAreaFiles.length > 5) {
      for (let index = this.notificationAreaFiles.length - 1; index >= 5; index--) {
        const file = this.notificationAreaFiles[index];
        this.notificationAreaFiles.splice(index);
        files.splice(index);
        errorFilesCountMoreThanFive.push(file.fileEntry.name);
      }

      this.mapError = 'Please select only a maximum of 5 files, alternatively you can zip the files. These files were skipped: ' + errorFilesCountMoreThanFive.join(', ');
      allFilesValid = false;
    }

    for (let index = 0; index < this.notificationAreaFiles.length; index++) {
      const droppedFile = this.notificationAreaFiles[index];

      if (!droppedFile.fileEntry.isFile) {
        const fileEntry = droppedFile.fileEntry as FileSystemDirectoryEntry;
        console.log(droppedFile.relativePath, fileEntry);
      } else {
        const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
        fileEntry.file((file: File) => {
          if (file.size > tenMegabytes) {
            this.notificationAreaFiles.splice(index);
            files.splice(index);
            allFilesValid = false;
            errorFilesOverTenMb.push(file.name);
            return;
          }

          if (!this.fileManagementService.validateMapMimeType(file) && this.fileManagementService.validateFileEndingTypes(file)) {
            this.notificationAreaFiles.splice(index);
            files.splice(index);
            allFilesValid = false;
            errorFilesWithIncorrectMimeTypes.push(file.name);
            return;
          }

          if (!this.fileManagementService.fileHasValidCharacters(file)) {
            this.notificationAreaFiles.splice(index);
            files.splice(index);
            allFilesValid = false;
            errorFilesWithIllegalCharacters.push(file.name);
            return;
          }

          validFilesToUpload.push(this.notificationAreaFiles[index]);
        });
      }
    }

    if (!allFilesValid) {
      this.uploadValidMapFiles(validFilesToUpload);
      this.mapError = '';

      if (errorFilesOverTenMb.length > 0) {
        this.mapError += 'The file(s) you are trying to attach is too large please zip the file before continuing. It must be less than 10Mb each. See: ' + errorFilesOverTenMb.join(', ');
      }

      if (errorFilesWithIncorrectMimeTypes.length > 0) {
        this.mapError += 'The file is an unacceptable type. Please select an acceptable file - ESRI shapefile (.ZIP, .RAR), MapInfo (.ZIP, .RAR), .DXF, .KML, .KMZ, TIFF, GeoTIFF, JPEG, GeoJPEG, Endas Imagine IMG format (BMP excluded), Arc Grid (.ZIP). See : ' + errorFilesWithIncorrectMimeTypes.join(', ');
      }

      if (errorFilesCountMoreThanFive.length > 0) {
        this.mapError = 'Please select only a maximum of 5 files, alternatively you can zip the files. These files were skipped: ' + errorFilesCountMoreThanFive.join(', ');
      }

      if (errorFilesWithIllegalCharacters.length > 0) {
        this.mapError = 'Please only upload files with names that consist of alphnumeric and _, @, ., #, &, +, -, or ~ ' + errorFilesWithIllegalCharacters.join(', ');
      }
    } else {
      this.uploadValidMapFiles(this.notificationAreaFiles);
      this.mapError = '';
    }
  }

  public fileOver(event: DragEvent) {
    console.log('File over');
    console.log(event);
  }

  public fileLeave(event: DragEvent) {
    console.log('File leave');
    console.log(event);
  }

  private uploadValidMapFiles(ngxFileDropEntries: NgxFileDropEntry[]) {
    this.fileManagementService
      .convertNgxFileDropEntriesToFileSystems$(ngxFileDropEntries)
      .pipe(
        untilDestroyed(this),
        concatMap((validFiles) => {
          if (validFiles.length > 0) {
            return this.fileManagementService.uploadMapFiles$(validFiles).pipe(
              first(),
              untilDestroyed(this),
              tap(() => {
                const mapFileNames: string[] = [];
                for (let index = 0; index < validFiles.length; index++) {
                  mapFileNames.push(validFiles[index].name);
                }

                this.mapFileNames = mapFileNames;
                this.mapProvidedControl?.setValue(true);
              }),
              concatMap(() => {
                const formModel = this.getNotificationAreaFormData();
                return this.notificationAreaService.sessionSetNotificationArea$(formModel);
              }),
            );
          }
          return of(null);
        }),
        catchError((err: unknown) => {
          this.logger.error('uploadValidMapFiles error', err);
          this.mapError = 'The file is an unacceptable type. Please select an acceptable file - ESRI shapefile (.ZIP, .RAR), MapInfo (.ZIP, .RAR), .DXF, .KML, .KMZ, TIFF, GeoTIFF, JPEG, GeoJPEG, Endas Imagine IMG format (BMP excluded), Arc Grid (.ZIP). ' + 'Please try again!';
          return of();
        }),
      )
      .subscribe();
  }

  override next(): void {
    this.nextButtonClicked = true;
    this.form.markAsDirty();
    this.isAreaWidthDirty = true;
    this.form.updateValueAndValidity();

    this.setCoastalResponseAndGetLatestAreaCap$()
      .pipe(
        first(),
        concatMap(() => {
          this.performValidations();
          if (!this.form.valid) {
            this.logger.error('There are Form validation errors in notification-area');
            this.utilService.getFormValidationErrors(this.form);
            return of(false);
          }

          if (this.howToProvideControl?.value === NotificationAreaLodgementTypeEnum.spatialDataCapture) {
            this.mapFileNames = [];
          } else {
            this.islodgmentinLotPlan$.next(null);
          }

          this.mapProvidedControl?.setValue(!!this.mapFileNames);

          let formModel = this.getNotificationAreaFormData();
          formModel.lineLength = this.currentLineLength;

          return this.vwds.setNotificationArea$(formModel);
        }),
      )
      .subscribe((success) => {
        if (success) {
          super.next();
        } else {
          this.logger.warning('Notification area Next button issue - cannot continue please check validations logics');
        }
      });
  }

  override cancel(): void {
    super.cancel();
  }

  private determineKoalaAreaLimitValidationAndMessage() {
    if (this.lotOnPlan.containsKoalaHabitatArea) {
      if (this.purpose.purposeCode === PurposeEnum.managingWeeds || this.purpose.purposeCode === PurposeEnum.managingRegrowthDensity) {
        if (this.areaControl?.value > this.lotOnPlan.area || this.areaControl?.value > this.koalaHabitatLimitAreaHectares) {
          this.isKoalaAreaValid = false;
          let areaHectaresLimit = this.lotOnPlan.area > this.koalaHabitatLimitAreaHectares ? this.koalaHabitatLimitAreaHectares : this.lotOnPlan.area;
          this.koalaClearingMessage = `The maximum area which can be cleared is ${areaHectaresLimit} hectares`;
        } else {
          this.isKoalaAreaValid = true;
          this.koalaClearingMessage = undefined;
        }
      } else {
        this.isKoalaAreaValid = true;
        this.koalaClearingMessage = undefined;
      }
    }
  }

  private getNotificationAreaFormData(): INotificationAreaModel {
    const formModel = this.form.getRawValue() as INotificationAreaModel;

    // add mapFileNames manually, as it's stored as variable
    formModel.mapFileNames = this.mapFileNames;

    return formModel;
  }
}
